import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import AddIcon from "@mui/icons-material/Add";
import { WardCreateEdit } from "./WardCreateEdit";
import { useWards } from "../../../../../hooks/query/useWards";
import { Ward } from "../../../../../interfaces/WardInterface";

// eslint-disable-next-line import/prefer-default-export
export function WardList(): React.ReactNode {
  const { data: wards = [], isLoading } = useWards();

  const [isCreateEditOpen, setIsCreateEditOpen] = useState(false);
  const [editingWard, setEditingWard] = useState<Ward | null>(null);

  const handleCreateNew = () => {
    setEditingWard(null);
    setIsCreateEditOpen(true);
  };

  const handleEdit = (ward: Ward) => {
    setEditingWard(ward);
    setIsCreateEditOpen(true);
  };

  const handleCreateEditSuccess = () => {
    setIsCreateEditOpen(false);
    setEditingWard(null);
  };

  const handleCreateEditClose = () => {
    setIsCreateEditOpen(false);
    setEditingWard(null);
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%"
        }}
      >
        <Typography fontWeight="500">Manage Wards</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew}>
          Add Ward
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Ward Name
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Short Code
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Type
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "15%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Building
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "15%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Floor
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          {wards.length > 0 && (
            <TableBody>
              {wards.map((ward) => (
                <TableRow key={ward.id} sx={{ height: "48px" }}>
                  <TableCell>
                    <Typography fontSize="13px">{ward.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px">{ward.shortCode}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px">{ward.type}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px">{ward.building ?? "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px">{ward.floor || "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                      <EditIcon
                        sx={{ cursor: "pointer", fontSize: "16px", color: "rgba(0, 0, 0, 0.54)" }}
                        onClick={() => handleEdit(ward)}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {isCreateEditOpen && (
        <WardCreateEdit
          onClose={handleCreateEditClose}
          onSuccess={handleCreateEditSuccess}
          ward={editingWard}
        />
      )}
    </Box>
  );
}
